import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { database } from '../../firebase';
import { ref, push, set, get, query, orderByChild, equalTo } from 'firebase/database';

const HeroSection = () => {
  const { ref: inViewRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false
  });
  
  const [showWishlistForm, setShowWishlistForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    location: '',
    referralSource: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const checkExistingSubmission = async (email) => {
    try {
      // Check if this email has already been submitted
      const wishlistRef = ref(database, 'wishlist');
      const emailQuery = query(
        wishlistRef,
        orderByChild('email'),
        equalTo(email)
      );
      
      const snapshot = await get(emailQuery);
      
      if (snapshot.exists()) {
        const lastSubmission = Object.values(snapshot.val())[0];
        const lastSubmissionTime = new Date(lastSubmission.timestamp).getTime();
        const currentTime = new Date().getTime();
        
        // If the last submission was less than 24 hours ago, reject
        const hoursSinceLastSubmission = (currentTime - lastSubmissionTime) / (1000 * 60 * 60);
        if (hoursSinceLastSubmission < 24) {
          return { exists: true, canSubmit: false };
        }
      }
      
      return { exists: false, canSubmit: true };
    } catch (error) {
      console.error("Error checking for existing submission:", error);
      return { exists: false, canSubmit: true };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      // Sanitize input data
      const sanitizedData = {
        email: formData.email.trim().toLowerCase(),
        fullName: formData.fullName ? formData.fullName.trim() : '',
        location: formData.location ? formData.location.trim() : '',
        referralSource: formData.referralSource,
        timestamp: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        userAgent: navigator.userAgent
      };
      
      // Create a new entry in the wishlist collection
      const wishlistRef = ref(database, 'wishlist');
      const newEntryRef = push(wishlistRef);
      
      // Save the data
      await set(newEntryRef, sanitizedData);
      
      // Success
      setSubmitted(true);
      
      // Reset form
      setFormData({
        email: '',
        fullName: '',
        location: '',
        referralSource: ''
      });
      
      // Hide the form after showing success message
      setTimeout(() => {
        setSubmitted(false);
        setShowWishlistForm(false);
      }, 3000);
    } catch (err) {
      console.error("Error submitting form:", err);
      setError('An error occurred while submitting. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <section id="hero" className="dark-gradient-hero pt-32 pb-16 md:pt-40 md:pb-24" ref={inViewRef}>
      <div className="container mx-auto px-6 text-center">
        <motion.div 
          className="flex justify-center mb-8"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: inView ? 1 : 0.8, opacity: inView ? 1 : 0 }}
          transition={{ type: 'spring', duration: 0.8 }}
        >
          <img 
            alt="Memic Logo" 
            className="w-40 h-30" 
            src="/images/hero_head_section.png" 
          />
        </motion.div>
        <motion.h1 
          className="text-4xl md:text-5xl font-bold text-white mb-6"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: inView ? 0 : 50, opacity: inView ? 1 : 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          Welcome to Memic!
        </motion.h1>
        <motion.p 
          className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-8"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: inView ? 0 : 50, opacity: inView ? 1 : 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          Transform your minions into lively AI avatars and embark on conversations filled with personality and charm. Get ready to bring your chats to life like never before!
        </motion.p>
        <motion.button 
          className="btn-dark text-white font-semibold py-3 px-8 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: inView ? 0 : 50, opacity: inView ? 1 : 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          whileHover={{ scale: 1.05, boxShadow: "0px 8px 15px rgba(0, 0, 0, 0.3)" }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowWishlistForm(true)}
        >
          Join the Wishlist
        </motion.button>
        
        <AnimatePresence>
          {showWishlistForm && (
            <motion.div 
              className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !isSubmitting && !submitted && setShowWishlistForm(false)}
            >
              <motion.div 
                className="bg-zinc-800 rounded-xl p-6 w-full max-w-md"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: 'spring', damping: 25 }}
                onClick={(e) => e.stopPropagation()}
              >
                {!submitted ? (
                  <>
                    <h2 className="text-2xl font-bold text-white mb-4">Join Our Wishlist</h2>
                    <p className="text-gray-300 mb-6">Be among the first to experience Memic when we launch!</p>
                    
                    {error && (
                      <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-200 text-sm">
                        {error}
                      </div>
                    )}
                    
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1 text-left">
                          Email Address <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          className="w-full px-4 py-2 bg-zinc-700 text-white rounded-lg focus:ring-2 focus:ring-white focus:outline-none"
                          disabled={isSubmitting}
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="fullName" className="block text-sm font-medium text-gray-300 mb-1 text-left">
                          Full Name
                        </label>
                        <input
                          type="text"
                          id="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          placeholder="John Doe"
                          className="w-full px-4 py-2 bg-zinc-700 text-white rounded-lg focus:ring-2 focus:ring-white focus:outline-none"
                          disabled={isSubmitting}
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="location" className="block text-sm font-medium text-gray-300 mb-1 text-left">
                          Location
                        </label>
                        <input
                          type="text"
                          id="location"
                          value={formData.location}
                          onChange={handleChange}
                          placeholder="City, Country"
                          className="w-full px-4 py-2 bg-zinc-700 text-white rounded-lg focus:ring-2 focus:ring-white focus:outline-none"
                          disabled={isSubmitting}
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="referralSource" className="block text-sm font-medium text-gray-300 mb-1 text-left">
                          How did you hear about us?
                        </label>
                        <select
                          id="referralSource"
                          value={formData.referralSource}
                          onChange={handleChange}
                          className="w-full px-4 py-2 bg-zinc-700 text-white rounded-lg focus:ring-2 focus:ring-white focus:outline-none"
                          disabled={isSubmitting}
                        >
                          <option value="">Please select</option>
                          <option value="social">Social Media</option>
                          <option value="search">Search Engine</option>
                          <option value="friend">Friend or Colleague</option>
                          <option value="blog">Blog or Article</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      
                      <div className="flex space-x-3 pt-2">
                        <motion.button
                          type="button"
                          className="flex-1 px-4 py-2 bg-zinc-600 text-white rounded-lg hover:bg-zinc-500 transition-colors"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setShowWishlistForm(false)}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </motion.button>
                        <motion.button
                          type="submit"
                          className="flex-1 px-4 py-2 bg-white text-black font-medium rounded-lg hover:bg-gray-200 transition-colors"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <span className="flex items-center justify-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Submitting...
                            </span>
                          ) : "Submit"}
                        </motion.button>
                      </div>
                    </form>
                  </>
                ) : (
                  <motion.div 
                    className="text-center py-8"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <motion.div 
                      className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', damping: 8 }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </motion.div>
                    <h3 className="text-xl font-bold text-white mb-2">Thank You!</h3>
                    <p className="text-gray-300">You've been added to our wishlist. We'll notify you when we launch!</p>
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default HeroSection; 